import React, {
  useRef,
  forwardRef,
  useMemo,
  useState,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import { Logger } from "@base-provider";
import getStyled from "../../hooks/getStyled";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import SFDataGridCore from "./dataGridCore";
import { nanoid } from "@reduxjs/toolkit";
import { CELL_RENDERER, HEADER_RENDERED } from "./dataGridConfig";
import _ from "lodash";

import { getGridData } from "../../services/dataGridService";

import SFDataGridSettingsButton from "./settingsDrawer/settingsButton";
import SFDataGridSettingsDrawer from "./settingsDrawer/";
import { getCustomComponents } from "./helpers";

const { styled } = getStyled();

const BoxStyled = styled(Box)(({ theme }) => ({
  "&": {
    border: "none",
    backgroundColor: "#F6F6FA",
    display: "flex",
    alignItems: "center",
    padding: "10px",
    borderRadius: "4px",
  },
}));

const SFDataGridClient = forwardRef(
  (
    {
      defaultRowIds = false,
      persistState = true,
      gridName,
      colDefs,
      items,
      customComponents = {},
      groupDefaultExpanded,
      filterPlaceholder,
      permissions,
      exportFileName,
      suppressRowHoverHighlight = false,
      onGroupChanged = null,
      groupByOptions,
      subBar,
      editMode,
      suppressFilterChips = true,
      rowDragging = false,
      disableDefaultRowDragger = false,
      onRowDragEnd = null,
      rowClassRules,
      detailRowClassRules,
      noRowsMessage = null,
      masterDetail,
      detailColumnDefs,
      getDetailRowData,
      detailsDataKey = "detailData",
      detailRowAutoHeight,
      viewMasterDetailsUrl,
      onRowDataUpdated,
      allowMultipleDetailRows = true,
      minHeight,
      domLayout = "autoHeight",
      hasColSelection = false,
      hasRowSelection: hasRowSelectionProp,
      ...otherProps
    },
    ref
  ) => {
    Logger.of("App.Core.Component.SFDataGridClient").info({ items, colDefs });
    const internalRef = useRef();

    const gridRef = ref ?? internalRef;

    const [columnsDrawerOpen, setColumnsDrawerOpen] = useState(false);

    //IF row dragging is enabled - sorting must be disabled
    const rowDraggingColDef = {
      width: 60,
      suppressSizeToFit: true,
      cellRenderer: CELL_RENDERER.ROW_DRAGGING,
    };

    const buildColumns = useCallback(
      (columns, type = "masterColumnDefs") => {
        Logger.of("App.Core.DataGridClient.buildColumns").info({
          gridName,
          type,
          columns,
        });
        // Move custom properties to context
        // Selection is now handled by the default AG Grid selection column via rowSelection.checkboxes
        const processedColumns = columns.map((colDef) => {
          // Extract all custom properties that should go to context (same as index.js)
          const {
            selection,
            tooltip,
            tooltipWidth,
            tooltipPosition,
            filterTooltip,
            useCustomHeader,
            tooltipVariant,
            onTooltipClick,
            showChips,
            align,
            variant,
            mustGrow,
            treeFilter,
            trueIcon,
            falseIcon,
            filterModel,
            alignItems,
            avatar,
            textVariant,
            textComponent,
            onCheck,
            styleOptions,
            supressFilterModelFromValues,
            justGrow,
            ignoreFilterOnGetValues,
            showBasicFilterDisabled,
            afterFilterChanged,
            nullValueFilterLabel,
            groupByColumn,
            allowGrow,
            groupFilter,
            treeStructure,
            filterField,
            treeFilterValuesUrl,
            boldText,
            isGroupFilter,
            rowHeight,
            persistBasicFiltersApply,
            ...validColDef
          } = colDef;
          return {
            ...validColDef,
            context: {
              ...colDef.context,
              selection,
              tooltip,
              tooltipWidth,
              tooltipPosition,
              filterTooltip,
              useCustomHeader,
              tooltipVariant,
              onTooltipClick,
              showChips,
              align,
              variant,
              mustGrow,
              treeFilter,
              trueIcon,
              falseIcon,
              filterModel,
              alignItems,
              avatar,
              textVariant,
              textComponent,
              onCheck,
              styleOptions,
              supressFilterModelFromValues,
              justGrow,
              ignoreFilterOnGetValues,
              showBasicFilterDisabled,
              afterFilterChanged,
              nullValueFilterLabel,
              groupByColumn,
              allowGrow,
              groupFilter,
              treeStructure,
              filterField,
              treeFilterValuesUrl,
              boldText,
              isGroupFilter,
              rowHeight,
              persistBasicFiltersApply,
              // Store original filter type for basicFiltering (before converting custom types to true)
              originalFilterType: colDef.filter,
            },
            headerComponent: !_.isNil(tooltip)
              ? HEADER_RENDERED.DEFAULT
              : "agColumnHeader",
            // Map custom filter types to valid AG Grid filters
            filter: ["category", "hierarchy", "dropdown", "boolean", "radio"].includes(colDef.filter)
              ? true
              : colDef.filter,
          };
        });
        if (hasColSelection) {
          processedColumns.push({
            field: "colSelection",
            headerName: "",
            headerComponent: () => (
              <SFDataGridSettingsButton
                onClick={() => setColumnsDrawerOpen(true)}
              />
            ),
            headerClass: "ag-center-aligned-header no-padding",
            cellClass: "ag-center-aligned-cell no-padding",
            pinned: "right",
            suppressColumnsToolPanel: true,
            suppressMovable: true,
            resizable: false,
            sortable: false,
            width: 55,
            maxWidth: 55,
          });
        }
        if (rowDragging && !disableDefaultRowDragger) {
          processedColumns.unshift(rowDraggingColDef);
        }
        return processedColumns;
      },
      [rowDragging, disableDefaultRowDragger, hasColSelection]
    );

    //Ag-grid suggests to use the useMemo hook to memoize the columnDefs array
    //we update the columnDefs array only when the columnDefs prop changes (need also to memoize out of this component) or the buildColumns function changes (which is wrapped in useCallback)
    const columnDefs = useMemo(
      () => buildColumns(colDefs),
      [colDefs, buildColumns]
    );
    const detailedColDefs = useMemo(
      () =>
        detailColumnDefs && buildColumns(detailColumnDefs, "detailColumnDefs"),
      [detailColumnDefs, buildColumns]
    );

    // Check if any column has selection enabled for row selection
    // Use prop if provided, otherwise check processed column definitions
    const hasRowSelection = useMemo(
      () =>
        hasRowSelectionProp ??
        columnDefs.some((col) => col.selection || col.context?.selection),
      [hasRowSelectionProp, columnDefs]
    );

    const searchChangeHandler = (e) => {
      gridRef.current.api.setGridOption("quickFilterText", e.target.value);
    };

    const getRowId = useCallback((params) => {
      if (otherProps.getRowId) {
        return otherProps.getRowId(params);
      }
      var data = params.data;
      // use nanoid in case row hasn't id (eg. row group)
      return data.id ? params.data.id : nanoid();
    }, []);

    const handleDrag = useCallback((e) => {
      if (onRowDragEnd) {
        onRowDragEnd(getCopiedRowData());
      }
    }, []);

    const getCopiedRowData = () => {
      let result = [];
      gridRef.current.api.forEachNode((node) => result.push({ ...node.data }));
      return result;
    };

    const getDetailServerSideDataSource = async (id) => {
      let response = await getGridData(
        viewMasterDetailsUrl.replace(/{id}/, id),
        {}
      );
      return response.data;
    };

    const detailCellRendererParams = useMemo(() => {
      return {
        ...otherProps.detailCellRendererParams,
        detailGridOptions: {
          ...otherProps.detailCellRendererParams?.detailGridOptions,
          columnDefs: detailedColDefs,
          components: {
            ...customComponents,
          },
          onGridReady: (params) => {},
          defaultColDef: {
            flex: 1,
          },
          context: {
            gridName: gridName,
            getCopiedRowData: getCopiedRowData,
          },
          rowClassRules: detailRowClassRules,
        },
        getDetailRowData: (params) => {
          if (getDetailRowData) {
            return params.successCallback(getDetailRowData(params));
          }
          // If viewMasterDetailsUrl is provided, fetch data from server
          if (viewMasterDetailsUrl) {
            getDetailServerSideDataSource(params.data.id).then((res) =>
              params.successCallback(res.rowData)
            );
          } else {
            // else return the data from detailsDataKey
            params.successCallback(params.data[detailsDataKey]);
          }
        },
      };
    }, []);

    //custom component should be memoized to avoid re-rendering
    const customRenderers = useMemo(
      () => getCustomComponents(customComponents),
      []
    );

    return (
      <>
        <SFDataGridCore
          {...otherProps}
          //enbable below for debug mode
          // debug
          persistState={persistState}
          rowData={items}
          ref={gridRef}
          search={
            !editMode && (
              <BoxStyled
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  height: "40px",
                }}
              >
                <SearchIcon fontSize="medium" />
                <InputBase
                  sx={{ ml: 2, flex: 1, fontSize: "14px" }}
                  onChange={searchChangeHandler}
                  placeholder={filterPlaceholder}
                />
              </BoxStyled>
            )
          }
          domLayout={domLayout}
          gridName={gridName}
          suppressRowHoverHighlight={suppressRowHoverHighlight}
          suppressCellFocus
          groupDefaultExpanded={groupDefaultExpanded}
          exportFileName={exportFileName}
          customRenderers={customRenderers}
          permissions={permissions}
          columnDefs={columnDefs}
          onGroupChanged={onGroupChanged}
          groupByOptions={groupByOptions}
          subBar={subBar}
          getRowId={getRowId}
          suppressFilterChips={suppressFilterChips}
          rowModelType={"clientSide"}
          rowDragging={rowDragging}
          onRowDragEnd={handleDrag}
          rowClassRules={rowClassRules}
          getCopiedRowData={getCopiedRowData}
          noRowsMessage={noRowsMessage}
          hasRowSelection={hasRowSelection}
          //enables master detail
          masterDetail={masterDetail}
          {...(masterDetail && {
            detailCellRendererParams,
          })}
          detailRowAutoHeight={detailRowAutoHeight}
          onRowDataUpdated={onRowDataUpdated}
          allowMultipleDetailRows={allowMultipleDetailRows}
          minHeight={minHeight}
        />
        {hasColSelection && (
          <SFDataGridSettingsDrawer
            open={columnsDrawerOpen}
            onClose={() => setColumnsDrawerOpen(false)}
            ref={gridRef}
          />
        )}
      </>
    );
  }
);

SFDataGridClient.propTypes = {
  groupByOptions: PropTypes.shape({
    groupByComponent: PropTypes.func,
    defaultGroupByLabel: PropTypes.node,
  }),
  colDefs: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      rowGroup: PropTypes.bool,
      hide: PropTypes.bool,
      headerName: PropTypes.string,
      headerComponentParams: PropTypes.shape({
        template: PropTypes.string,
      }),
      cellRenderer: PropTypes.string,
      cellRendererParams: PropTypes.shape({
        requiredProficiencyLevel: PropTypes.string,
      }),
      suppressSizeToFit: PropTypes.bool,
      filter: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
      width: PropTypes.number,
      suppressColumnsToolPanel: PropTypes.bool,
    }).isRequired
  ).isRequired,
  items: PropTypes.array,
  customComponents: PropTypes.object,
  groupDefaultExpanded: PropTypes.number,
  groupDisplayType: PropTypes.string,
  filterPlaceholder: PropTypes.string,
  exportFileName: PropTypes.string,
  permissions: PropTypes.shape({
    export: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    view: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
  }),
  // Message to display when there are no rows
  noRowsMessage: PropTypes.string,
  rowClassRules: PropTypes.object,
  detailRowClassRules: PropTypes.object,
  viewMasterDetailsUrl: PropTypes.string,
  onRowDataUpdated: PropTypes.func,
  allowMultipleDetailRows: PropTypes.bool,
  minHeight: PropTypes.number,
  hasColSelection: PropTypes.bool,
  // If true, the default raw dragger will not be used (useful when we want a custom row dragger inside a cell renderer)
  disableDefaultRowDragger: PropTypes.bool,
};

export default SFDataGridClient;
